import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2Icon, PenLineIcon } from 'lucide-react';
import { ListPageLayout } from '../components/lists/ListPageLayout';
import { LogCommunicationModal } from '../components/communication/LogCommunicationModal';
import { useToast } from '../contexts/ToastContext';
import { preApprovals } from '../data/preApprovals';
import type { CommunicationSubject, PreApprovalStatus } from '../types/portal';
const filters = [{
  id: 'all',
  label: 'All'
}, {
  id: 'ready-to-review',
  label: 'Ready to review'
}, {
  id: 'docs-outstanding',
  label: 'Docs outstanding'
}, {
  id: 'expiring',
  label: 'Expiring'
}, {
  id: 'weekly-touch',
  label: 'Weekly touch due'
}];
const statusMeta: Record<PreApprovalStatus, {
  label: string;
  classes: string;
}> = {
  'ready-to-review': {
    label: 'Ready to review',
    classes: 'bg-warn-50 text-warn-700 ring-warn-200'
  },
  'docs-outstanding': {
    label: 'Docs outstanding',
    classes: 'bg-warn-50 text-warn-700 ring-warn-200'
  },
  expiring: {
    label: 'Expiring soon',
    classes: 'bg-danger-50 text-danger-700 ring-danger-200'
  },
  approved: {
    label: 'Approved',
    classes: 'bg-success-50 text-success-700 ring-success-200'
  }
};
export function MyPreApprovalsPage() {
  const {
    showToast
  } = useToast();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [subject, setSubject] = useState<CommunicationSubject | null>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 600);
    return () => window.clearTimeout(timer);
  }, []);
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return preApprovals.filter(item => {
      const matchesFilter = filter === 'all' ? true : filter === 'weekly-touch' ? !item.weeklyTouchDone : item.status === filter;
      const matchesSearch = term.length === 0 || item.borrower.toLowerCase().includes(term) || item.id.toLowerCase().includes(term) || item.realtor.toLowerCase().includes(term);
      return matchesFilter && matchesSearch;
    });
  }, [filter, search]);
  const filterChips = filters.map(item => ({
    ...item,
    count: item.id === 'all' ? preApprovals.length : item.id === 'weekly-touch' ? preApprovals.filter(record => !record.weeklyTouchDone).length : preApprovals.filter(record => record.status === item.id).length
  }));
  return <>
      <ListPageLayout title="My Pre-Approvals" subtitle="Every pre-approved borrower should get a logged touch each week." searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search by borrower, ID, or Realtor" filters={filterChips} activeFilter={filter} onFilterChange={setFilter} loading={loading} isEmpty={visible.length === 0} emptyTitle="Nothing in this view" emptyMessage="All pre-approvals in this filter are handled. New submissions land here as soon as they’re issued." resultLabel={`Showing ${visible.length} of ${preApprovals.length} pre-approvals`} data-id="element-733" data-mp="yax1c1tt97j7">
        <ul className="divide-y divide-navy-100" data-id="element-734" data-mp="27cqfsysrcvy2">
          {visible.map(item => {
          const meta = statusMeta[item.status];
          return <li key={item.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-735" data-mp="2cdxnkxuwhv4q">
                <div className="min-w-0" data-id="element-736" data-mp="2ma8xjh70q8w0">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-737" data-mp="2qbchofw3mr31">
                    <p className="text-sm font-semibold text-navy-900" data-id="element-738" data-mp="2q98czvqdlo1p">
                      {item.borrower}
                    </p>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ${meta.classes}`} data-id="element-739" data-mp="vx1o5u4k78qx">
                      {meta.label}
                    </span>
                    {item.weeklyTouchDone ? <span className="inline-flex items-center gap-1 rounded-full bg-success-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-success-700 ring-1 ring-success-200" data-id="element-740" data-mp="alknxhuqkx4r">
                        <CheckCircle2Icon className="h-3 w-3" aria-hidden="true" data-id="element-741" data-mp="3e19pib3t5lxp" />
                        Weekly touch done
                      </span> : <span className="inline-flex items-center rounded-full bg-warn-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-warn-700 ring-1 ring-warn-200" data-id="element-742" data-mp="3ntns162pmwkx">
                        Weekly touch due
                      </span>}
                  </div>
                  <p className="mt-1 text-sm text-navy-700" data-id="element-743" data-mp="2qamaz6zdzy26">
                    {item.amount} · {item.program} · DTI {item.dti} · Credit{' '}
                    {item.credit}
                  </p>
                  <p className="mt-1 text-xs text-navy-500" data-id="element-744" data-mp="1amzb8yu1gc87">
                    {item.id} · {item.expiresLabel} · {item.lastTouchLabel} ·{' '}
                    {item.realtor}
                  </p>
                </div>

                <div className="flex shrink-0 gap-2" data-id="element-745" data-mp="1ol5q0wnapli0">
                  <button type="button" onClick={() => setSubject({
                id: item.id,
                name: item.borrower,
                context: `${item.amount} · ${item.program} · ${item.expiresLabel}`,
                goalCredit: 'your weekly pre-approval touch goal'
              })} className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-700 sm:flex-none" data-id="element-746" data-mp="1olp4g3dqlrra">
                    <PenLineIcon className="h-4 w-4" aria-hidden="true" data-id="element-747" data-mp="25u86bdl01b4z" />
                    Log communication
                  </button>
                  <button type="button" onClick={() => showToast(`Opening pre-approval ${item.id}.`)} className="inline-flex h-11 min-w-[5.5rem] items-center justify-center rounded-lg border border-navy-200 px-3 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-748" data-mp="212ghp8lelfhh">
                    Review
                  </button>
                </div>
              </li>;
        })}
        </ul>
      </ListPageLayout>

      <LogCommunicationModal subject={subject} onClose={() => setSubject(null)} onLogged={showToast} data-id="element-749" data-mp="1e550htdgrsq8" />
    </>;
}